import React from 'react';
import { Box, Collapse, IconButton, List, ListItemButton, ListItemText, Stack, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircleIcon from '@mui/icons-material/Circle';
import { useOnboarding } from '../onboarding/OnboardingProvider';
const PANEL_BACKGROUND = '#f7f9f7';
const PANEL_TEXT = '#24352a';
const PANEL_ACCENT = '#2e7d32';
const NUTRIENT_TYPES = [
    { key: 'nitrogen', label: 'Азот' },
    { key: 'phosphorus', label: 'Фосфор' },
    { key: 'potassium', label: 'Калий' },
];
const ANALYTICAL_TYPES = [
    { key: 'ndvi', label: 'NDVI' },
    { key: 'ndmi', label: 'NDMI' },
    { key: 'gndvi', label: 'GNDVI' },
];
const SectionHeader = ({ label, open, onClick, onboardingAnchor }) => (React.createElement(Stack, { "data-onboarding-anchor": onboardingAnchor, direction: "row", justifyContent: "space-between", alignItems: "center", onClick: onClick, sx: {
        px: 1.5, py: 1, cursor: 'pointer', color: PANEL_TEXT,
        backgroundColor: PANEL_BACKGROUND, borderBottom: '1px solid #e2e8e3',
        '&:hover': { backgroundColor: '#edf3ee' }
    } },
    React.createElement(Typography, { variant: "body2", fontWeight: 600 }, label),
    React.createElement(ExpandMoreIcon, { fontSize: "small", color: "action", sx: { transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' } })));
export default function RightMapPanel({ token, activeLayer, onSelect, collapsed, onToggleCollapse, width, onResize, planningError }) {
    const { registerSafeAction } = useOnboarding();
    const [analyticalOpen, setAnalyticalOpen] = React.useState(false);
    const analyticalOpenRef = React.useRef(analyticalOpen);
    analyticalOpenRef.current = analyticalOpen;
    const [tasksOpen, setTasksOpen] = React.useState(false);
    const [fertilizationOpen, setFertilizationOpen] = React.useState(false);
    React.useEffect(() => registerSafeAction('field-expand-analytical', () => {
        const previous = analyticalOpenRef.current;
        analyticalOpenRef.current = true;
        setAnalyticalOpen(true);
        return () => {
            if (analyticalOpenRef.current !== true)
                return;
            analyticalOpenRef.current = previous;
            setAnalyticalOpen(previous);
        };
    }), [registerSafeAction]);
    const widthRef = React.useRef(width);
    widthRef.current = width;
    const lastXRef = React.useRef(0);
    const onResizeRef = React.useRef(onResize);
    onResizeRef.current = onResize;
    const handleMouseMove = React.useCallback((e) => {
        // Ручка на левом краю панели — движение мыши влево увеличивает ширину.
        const delta = e.clientX - lastXRef.current;
        lastXRef.current = e.clientX;
        onResizeRef.current(widthRef.current - delta);
    }, []);
    const handleMouseUp = React.useCallback(() => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);
    const startResize = (e) => {
        e.preventDefault();
        lastXRef.current = e.clientX;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };
    const selectAnalytical = (type) => {
        if ((activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'analytical' && activeLayer.type === type) {
            onSelect(null);
        }
        else {
            onSelect({ kind: 'analytical', type });
        }
    };
    if (collapsed) {
        return (React.createElement(Box, { sx: {
                width: 28, height: '100%', display: 'flex', alignItems: 'flex-start',
                justifyContent: 'center', pt: 1, backgroundColor: PANEL_BACKGROUND,
                borderLeft: '1px solid #e2e8e3'
            } },
            React.createElement(Tooltip, { title: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u043A\u0430\u0440\u0442\u044B \u043F\u043E\u043B\u044F" },
                React.createElement(IconButton, { size: "small", onClick: onToggleCollapse, sx: { color: PANEL_ACCENT } },
                    React.createElement(ChevronLeftIcon, { fontSize: "small" })))));
    }
    return (React.createElement(Box, { sx: {
            width, flexShrink: 0, position: 'relative', height: '100%',
            overflowY: 'auto', backgroundColor: PANEL_BACKGROUND, borderLeft: '1px solid #e2e8e3'
        } },
        React.createElement(Box, { onMouseDown: startResize, sx: {
                position: 'absolute', left: '-3px', top: 0, bottom: 0, width: '6px',
                cursor: 'col-resize', zIndex: 1,
                backgroundColor: 'transparent',
                transition: 'background-color 0.15s',
                '&:hover': { backgroundColor: 'rgba(18, 113, 71, 0.35)' }
            } }),
        React.createElement(Box, { sx: {
                height: 48, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: PANEL_BACKGROUND, color: PANEL_TEXT, position: 'relative', borderBottom: '1px solid #e2e8e3'
            } },
            React.createElement(IconButton, { size: "small", onClick: onToggleCollapse, sx: { position: 'absolute', top: '50%', left: 4, transform: 'translateY(-50%)', color: PANEL_ACCENT } },
                React.createElement(ChevronRightIcon, { fontSize: "small" })),
            React.createElement(Typography, { variant: "subtitle1", fontWeight: 700 }, "\u041A\u0430\u0440\u0442\u044B \u043F\u043E\u043B\u044F")),
        React.createElement(SectionHeader, { label: "\u0410\u043D\u0430\u043B\u0438\u0442\u0438\u0447\u0435\u0441\u043A\u0438\u0435 \u043A\u0430\u0440\u0442\u044B", open: analyticalOpen, onClick: () => setAnalyticalOpen(!analyticalOpen), onboardingAnchor: "field-analytical-maps" }),
        React.createElement(Collapse, { in: analyticalOpen },
            React.createElement(List, { dense: true, disablePadding: true }, ANALYTICAL_TYPES.map(({ key, label }) => (React.createElement(ListItemButton, { key: key, selected: (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'analytical' && activeLayer.type === key, onClick: () => selectAnalytical(key), sx: {
                    minHeight: 38,
                    '&.Mui-selected': { backgroundColor: '#e8f5e9', color: PANEL_ACCENT, fontWeight: 600 },
                    '&.Mui-selected:hover': { backgroundColor: '#dcefe0' }
                } },
                React.createElement(CircleIcon, { sx: { fontSize: 8, mr: 1, color: (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'analytical' && activeLayer.type === key ? PANEL_ACCENT : 'text.secondary' } }),
                React.createElement(ListItemText, { primary: label })))))),
        planningError && React.createElement(Typography, { variant: "caption", color: "error", sx: { display: 'block', p: 1.5 } }, planningError),
        React.createElement(SectionHeader, { label: "\u041A\u0430\u0440\u0442\u044B-\u0437\u0430\u0434\u0430\u043D\u0438\u044F", open: tasksOpen, onClick: () => setTasksOpen(!tasksOpen) }),
        React.createElement(Collapse, { in: tasksOpen },
            React.createElement(List, { dense: true, disablePadding: true },
                React.createElement(ListItemButton, { selected: (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'task' && activeLayer.type === 'fertility', sx: { minHeight: 38, '&.Mui-selected': { backgroundColor: '#e8f5e9', color: PANEL_ACCENT } }, onClick: () => {
                        if ((activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'task' && activeLayer.type === 'fertility') {
                            onSelect(null);
                        }
                        else {
                            onSelect({ kind: 'task', type: 'fertility' });
                        }
                    } },
                    React.createElement(ListItemText, { primary: "\u041A\u0430\u0440\u0442\u0430 \u043F\u043B\u043E\u0434\u043E\u0440\u043E\u0434\u0438\u044F \u043F\u043E\u0447\u0432\u044B" })),
                React.createElement(ListItemButton, { selected: (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'task' && activeLayer.type !== 'fertility', onClick: () => setFertilizationOpen(!fertilizationOpen), sx: { minHeight: 38, '&.Mui-selected': { backgroundColor: '#e8f5e9', color: PANEL_ACCENT } } },
                    React.createElement(ListItemText, { primary: "\u0414\u0438\u0444\u0444\u0435\u0440\u0435\u043D\u0446\u0438\u0440\u043E\u0432\u0430\u043D\u043D\u043E\u0435 \u0432\u043D\u0435\u0441\u0435\u043D\u0438\u0435 \u0443\u0434\u043E\u0431\u0440\u0435\u043D\u0438\u0439" }),
                    React.createElement(ExpandMoreIcon, { fontSize: "small", sx: { transform: fertilizationOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' } })),
                React.createElement(Collapse, { in: fertilizationOpen }, NUTRIENT_TYPES.map(({ key, label }) => (React.createElement(ListItemButton, { key: key, selected: (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'task' && activeLayer.type === key, sx: { pl: 4, minHeight: 36, '&.Mui-selected': { backgroundColor: '#e8f5e9', color: PANEL_ACCENT } }, onClick: () => {
                        if ((activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'task' && activeLayer.type === key) {
                            onSelect(null);
                        }
                        else {
                            onSelect({ kind: 'task', type: key });
                        }
                    } },
                    React.createElement(ListItemText, { primary: label })))))))));
}
